import { clamp } from '@pkg/utils';

// ALPHA

// Returns RGB color as RGBA with given alpha (between 0 and 1).
function rgbToRgba(color: string, opacity: number) {
    const clampedValue = clamp(0, opacity, 1);
    const opacityAsHex = Math.round(clampedValue * 255).toString(16);

    // TODO: This should be using leftPadNumber()...
    // the use of `toString(16)` should be revised.
    const finalValue = opacityAsHex.length === 1 ? `0${opacityAsHex}` : opacityAsHex;

    return `${color}${finalValue}`;
}

// SHARED COLORS

const WHITE = '#ffffff';
const BLACK = '#000000';
const GREEN = '#62fa71';
const PURPLE_LIGHT = '#976af6';
const PURPLE = '#523DF1';
const RED = '#EE0A4A';

// SHARED EASES

export const EASE_ON = [0.2, 0.85, 0.45, 1.0];
export const EASE_OUT = [0.45, 0.0, 0.85, 0.2];
export const EASE_CUBIC = [0.6, 0, 0.2, 1];

// DEFAULT THEME

const THEME = {
    colors: {
        black: BLACK,
        darkBg: '#090719',
        offBlack: '#1b1d22',
        green: GREEN,
        greenDark: '#009E54',
        purpleLight: PURPLE_LIGHT,
        purple: PURPLE,
        purpleDark: '#17061c',
        pink: '#fa06ff',
        blue: '#2a1f7c',
        blueDark: '#060b33',
        red: RED,
        grey: '#c4c4c4',
        white: WHITE,
        blueMagenta: '#2F273E',
        transparent: 'transparent',
        carbon: '#2a2637',

        // These colours below are from the Emotion provider which is deprecated.
        // Todo: review which colours are preferable? the emotions red or the default red? etc.
        // Emotion colours were used the design system Button and Typography components

        // black: '#090719',
        // green: '#62FA71',
        // grey: '#2A2637',
        // purple: '#523DF1',
        // red: '#EE0A4A',
    },
    shades: {
        black: {
            '0': rgbToRgba(BLACK, 0.0),
            '5': rgbToRgba(BLACK, 0.05),
            '10': rgbToRgba(BLACK, 0.1),
            '20': rgbToRgba(BLACK, 0.2),
            '30': rgbToRgba(BLACK, 0.3),
            '55': rgbToRgba(BLACK, 0.55),
            '70': rgbToRgba(BLACK, 0.7),
            '85': rgbToRgba(BLACK, 0.85),
            '90': rgbToRgba(BLACK, 0.9),
            '100': BLACK,
        },
        white: {
            '4': rgbToRgba(WHITE, 0.04),
            '5': rgbToRgba(WHITE, 0.05),
            '7': rgbToRgba(WHITE, 0.07),
            '10': rgbToRgba(WHITE, 0.1),
            '15': rgbToRgba(WHITE, 0.15),
            '20': rgbToRgba(WHITE, 0.2),
            '30': rgbToRgba(WHITE, 0.3),
            '35': rgbToRgba(WHITE, 0.35),
            '40': rgbToRgba(WHITE, 0.4),
            '50': rgbToRgba(WHITE, 0.5),
            '80': rgbToRgba(WHITE, 0.8),
            '100': WHITE,
        },
        green: {
            '20': rgbToRgba(GREEN, 0.1),
            '60': rgbToRgba(GREEN, 0.6),
            '100': GREEN,
        },
        purpleLight: {
            '90': rgbToRgba(PURPLE_LIGHT, 0.9),
            '100': PURPLE_LIGHT,
        },
        purple: {
            '5': rgbToRgba(PURPLE, 0.05),
            '20': rgbToRgba(PURPLE, 0.2),
            '50': rgbToRgba(PURPLE, 0.5),
            '60': rgbToRgba(PURPLE, 0.6),
            '70': rgbToRgba(PURPLE, 0.7),
            '100': PURPLE,
        },
        red: {
            '10': rgbToRgba(RED, 0.1),
            '60': rgbToRgba(RED, 0.6),
            '80': rgbToRgba(RED, 0.8),
            '90': rgbToRgba(RED, 0.9),
        },
    },
    fonts: {
        alliance:
            "'Alliance No.1', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
    },
    // TODO: Do we actually care about different animation settings per-theme?
    // The only reason this would make sense is to globally set something for
    // "prefers-reduced-motion" users. Given the aesthetic of our product...
    // is that really an audience that we are accommodating?
    transitions: {
        eases: {
            on: EASE_ON,
            out: EASE_OUT,
            cubic: EASE_CUBIC,
        },
        speeds: {
            rapid: 0.15,
            fast: 0.25,
            normal: 0.5,
            slow: 1,
            sloth: 2,
        },
        easesCss: {
            on: `cubic-bezier(${EASE_ON})`,
            out: `cubic-bezier(${EASE_OUT})`,
            cubic: `cubic-bezier(${EASE_CUBIC})`,
        },
    },
    // TODO: Why do we have another (slightly different) set of breakpoints
    // from `@pkg/providers/MediaQueryProvider/breakpoints.ts`?
    // Why would we even want to set different `breakpoints` per theme?
    breakpoints: {
        mobileSmall: '576px',
        mobile: '768px',
        tabletSmall: '1064px',
        tablet: '1200px',
        desktopSmall: '1360px',
        desktop: '1920px',
    },
    zIndex: {
        yatPage: {
            nftFilterModal: 200,
            nftPreviewModal: 201,
        },
    },
    alerts: {
        error: {
            background: '#350625',
            outline: '#E60256',
        },
        general: {
            background: '#242424',
            outline: '#E0E0E0',
        },
        success: {
            background: '#19362C',
            outline: '#009E54',
        },
    },
    gradients: {
        brdCard: {
            default: 'linear-gradient(73.61deg, #FF5193 0.67%, #F9A43A 93.34%)',
        },
        epic: {
            default: 'linear-gradient(97.81deg, #03A859 0%, #75CB40 49.48%, #F7FC14 100%)',
            hover: 'linear-gradient(0deg, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0.4)), linear-gradient(97.81deg, #24EC8C 0%, #5DBA24 49.48%, #EDFF18 100%)',
        },
        legendary: {
            default: 'linear-gradient(97.81deg, #AA039F 0%, #ED014D 49.48%, #F67916 100%)',
            hover: 'linear-gradient(0deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6)), linear-gradient(97.81deg, #AA039F 0%, #ED014D 49.48%, #F67916 100%)',
        },
        tile: {
            default: 'linear-gradient(224.27deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 72.49%), #1C1A29',
        },
    },
    shadows: {
        epic: {
            default: '0 0 40px rgba(102, 202, 75, 0.6)',
            hover: '0 0 40px rgba(161, 209, 94, 0.8)',
        },
        legendary: {
            default: '0 0 40px rgba(231, 2, 86, 0.6)',
            hover: '0 0 40px rgba(247, 2, 126, 0.8)',
        },
        common: {
            default: '0 0 40px rgba(82, 61, 241, 0.6)',
            hover: '0 0 40px rgba(131, 98, 249, 0.8)',
        },
        rythmScore: {
            default: '0 0 40px rgba(255, 255, 255, 0.6)',
        },
    },
} as const;

export type ThemeType = typeof THEME;

export default THEME;
